import { insm } from '@atlaskit/insm';
import traceUFOPageload from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';

import { browserMetrics } from '@atlassian/browser-metrics/lib';

import { pauseLowPriorityEvents } from '@confluence/analytics-event-delay';
import { expVal } from '@confluence/feature-experiments';
import { networkTracker } from '@confluence/network';

import { resetAppKeys } from './AppKeys.ts';
import { browserMetricsState } from './utils/index.ts';

/**
 * Function that begins a browser-metrics page loading timer for the current navigation.
 * Intended to be plugged into the RouteManager onRoute() callback.
 * If a page loading timer has already been started but is unfinished, this function cancels and replaces it.
 *
 * @param routeName The matched route name
 * @param transitionId The current transition ID (0 means initial)
 */
export const startBrowserMetricsPageLoad = (
	routeName = '',
	ufoName: string | undefined = routeName,
	transitionId: number,
	contentId = '',
): void => {
	// Begin the loading timer _immediately_ using BM libraries generic meta startPageLoad metric.
	// Routes should later call stopPageLoad on the actual PageLoadMetric that is specifically defined for
	// the route in order to claim this page load.
	browserMetrics.startPageLoad({ isInitial: transitionId === 0 });

	networkTracker.startPageLoadTracking({ isInitial: transitionId === 0 });

	// Clear 3P app-key instrumentation so the page-load `appKeys` attribute only
	// reflects apps rendered on the page being navigated to, rather than
	// accumulating every app seen since the session started.
	//
	// Deliberately guarded on `transitionId !== 0`: the legacy `connectAppKeys`
	// attribute is only emitted when `transitionId === 0`, so this reset can
	// never affect it.
	if (transitionId !== 0 && expVal('confluence_app_keys_transition', 'isEnabled', false)) {
		resetAppKeys();
	}

	if (transitionId === 0) {
		traceUFOPageload(ufoName.toLocaleLowerCase());
		insm.start(ufoName.toLocaleLowerCase(), {
			initial: true,
			contentId,
		});
		insm.startHeavyTask('PageLoad');
	} else {
		traceUFOTransition(ufoName.toLocaleLowerCase());
		insm.start(ufoName.toLocaleLowerCase(), {
			initial: false,
			contentId,
		});
		insm.startHeavyTask('PageLoad');
	}

	performance.mark('browser-metric.start');

	const pageLoadMetric = browserMetrics.getPageLoadMetric();

	browserMetricsState.transitionId = transitionId;
	browserMetricsState.pageLoadStartTimestamp = pageLoadMetric.getData().start ?? performance.now();

	browserMetricsState.previousRoute = browserMetricsState.routeName;
	browserMetricsState.routeName = routeName;
	browserMetrics.setRoute(routeName);

	browserMetricsState.previousContentId = browserMetricsState.contentId;
	browserMetricsState.contentId = contentId;

	// will pause low priority events to avoid impacting page load performance
	// once the pageLoadEnd is triggered the resumeLowPriorityEvents will be called
	void pauseLowPriorityEvents(routeName);

	// All measurements are added to UFO via the interop layer, so nothing
	// needs to happen in the onStop callback anymore.
	pageLoadMetric.onStop(
		() => {},
		() => {},
	);
};
